import {
  infiniteQueryOptions,
  queryOptions,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import type {
  IProjectTemplatesListQuery,
  IProjectTemplatesPage,
  ProjectTemplateService,
} from "@/services/project-template.service";
import type { TProjectTemplateListItem } from "@/types/project-template";
import { getQueryClient } from "./client";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, scopeIfReady, type QueryScope } from "./scope";

/**
 * The project templates this caller may USE, `GET {ws}/project-templates/pickable/`,
 * as one shared reference query (NG-2799), `[slug, uid, 'project-templates', 'pickable']`.
 * The create-project dialog's and the request convert modal's template picker
 * offer these, and both prefill the form from the picked row.
 *
 * - **Reference** (contract §2): reused for 5 min, refetched on focus and
 *   reconnect. Today the picker re-reads the list every time it mounts.
 * - **Retries are `runRead`'s** (`"safe-read"`): a plain idempotent GET.
 *
 * The settings table's pages are their own family below (NG-2805), under
 * `'project-template-pages'`, so a write that marks the pickers stale does not
 * re-sort the table under the person editing it.
 *
 * @writers `ProjectTemplatesStore`'s create, duplicate, update, custom-field,
 * delete and bulk-delete writers mark it stale after a confirmed write
 * (`staleCachedTemplates`). Another admin's edit is seen when the entry goes
 * stale, until the backend hint lands (NG-2799 PR 6).
 */

/** The prefix every cached template read sits under. */
export function projectTemplatesKey(scope: QueryScope) {
  return scopedKey(scope, "project-templates");
}

export function pickableTemplatesQuery(
  service: Pick<ProjectTemplateService, "listPickableTemplates">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: scopedKey(scope, "project-templates", "pickable"),
    queryFn: scopedRead((request) => service.listPickableTemplates(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * One template's detail, `GET {ws}/projects/templates/{id}/`, as
 * `[slug, uid, 'project-template', id]`: what the template detail pages (the
 * layout, Overview, Work items, Workflow) and their breadcrumb read. Under
 * `'project-template'`, not `'project-templates'`, so a list write's prefix
 * invalidation leaves an open editor's detail alone.
 *
 * - **Reference** freshness, not mutable: each tab hydrates its form once per
 *   template, so a re-read only refreshes what is read live (the name, the
 *   view-only verdict), and a template's payload is too large to poll every
 *   60 s while someone edits it. The layout's loader reads it fresh on every
 *   entry.
 *
 * @writers `ProjectTemplatesStore`'s create, duplicate, update and work-item
 * custom-field writers put their response here (`patchCachedTemplate`); a
 * delete removes the entry.
 */
export function templateDetailKey(scope: QueryScope, templateId: string) {
  return scopedKey(scope, "project-template", templateId);
}

export function templateDetailQuery(
  service: Pick<ProjectTemplateService, "getTemplate">,
  scope: QueryScope,
  templateId: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: templateDetailKey(scope, templateId),
    queryFn: scopedRead((request) => service.getTemplate(scope.tenant, templateId, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * The settings Project Templates table's pages (NG-2805): Admin Settings →
 * Project Templates, and a team's Settings → Project Templates tab (`team`),
 * walked by cursor,
 * `[slug, uid, 'project-template-pages', {q, privacy, order_by, team}]`.
 *
 * - **Mutable** (contract §2): reused for 30 s and re-read on screen.
 * - Not under `'project-templates'`: the table keeps a duplicated template at
 *   the top where the person made it, rather than re-sorting it away the
 *   moment the pickers are marked stale.
 *
 * @writers `ProjectTemplatesStore` (`patchTemplatePages`): a duplicate puts
 * the copy at the top of every cached table, an edit patches its row, a
 * delete removes it; each then marks the pages stale without re-reading the
 * ones on screen, so the next visit reads the server's order. A create marks
 * them stale too.
 */

/** The table's server filters: search, privacy codes, sort and the team tab's team. */
export type TemplatesPageQuery = Omit<IProjectTemplatesListQuery, "cursor" | "per_page">;

/** Every cached table page in `scope`, whatever its filter. */
export function templatesPagesPrefix(scope: QueryScope) {
  return scopedKey(scope, "project-template-pages");
}

/**
 * The table key. Absent and empty parameters send the same request, so they
 * share an entry; `privacy` is a set, so its order does not split one.
 */
function templatesPageKey(scope: QueryScope, query: TemplatesPageQuery = {}) {
  return scopedKey(scope, "project-template-pages", {
    q: query.q ?? "",
    privacy: [...(query.privacy ?? [])].sort((a, b) => a - b),
    order_by: query.order_by ?? "",
    team: query.team ?? "",
  });
}

/**
 * The table's pages. The first page asks with no cursor; each later page asks
 * with the cursor the previous one returned.
 */
export function templatesPageQuery(
  service: Pick<ProjectTemplateService, "listTemplatesPage">,
  scope: QueryScope,
  query: TemplatesPageQuery = {},
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: templatesPageKey(scope, query),
    initialPageParam: null as string | null,
    queryFn: scopedRead<IProjectTemplatesPage, string | null>(
      (request, { pageParam }) =>
        service.listTemplatesPage(scope.tenant, { ...query, cursor: pageParam }, request),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: IProjectTemplatesPage) =>
      last.next_page_results && last.next_cursor ? last.next_cursor : undefined,
  });
}

/**
 * Warm the table's first page for `query` from a route loader, so the table
 * answers from the cache on arrival. Rejects when the read fails, so the
 * loader can log it; the table shows its own error from the same entry.
 */
export async function prefetchTemplatesPage(
  service: Pick<ProjectTemplateService, "listTemplatesPage">,
  slug: string,
  query: TemplatesPageQuery = {},
): Promise<void> {
  const scope = scopeIfReady(slug, "project templates page");
  if (!scope) return;
  await getQueryClient().fetchInfiniteQuery(templatesPageQuery(service, scope, query));
}

/** Every template in the pages, once each: a row can reappear on the next page as rows are added. */
export function rowsOfTemplatesPages(
  pages: readonly IProjectTemplatesPage[],
): TProjectTemplateListItem[] {
  const seen = new Set<string>();
  const rows: TProjectTemplateListItem[] = [];
  for (const page of pages) {
    for (const template of page.results) {
      if (seen.has(template.id)) continue;
      seen.add(template.id);
      rows.push(template);
    }
  }
  return rows;
}

/**
 * Apply a confirmed write to every cached table page in `scope`: `change` maps
 * one page's rows (and is told whether it is the first page, where a new row
 * goes), and `countDelta` moves each table's total. Then mark the pages stale
 * without re-reading the ones on screen, so the next visit reads the server's
 * order and filters. A read in flight is cancelled first, so an answer that
 * predates the write cannot land over it. Never rejects: a failed re-read is
 * the cache's to log.
 */
export async function patchTemplatePages(
  client: QueryClient,
  scope: QueryScope,
  change: (rows: TProjectTemplateListItem[], firstPage: boolean) => TProjectTemplateListItem[],
  countDelta: (data: InfiniteData<IProjectTemplatesPage>) => number,
): Promise<void> {
  const prefix = templatesPagesPrefix(scope);
  await client.cancelQueries({ queryKey: prefix });
  client.setQueriesData<InfiniteData<IProjectTemplatesPage>>({ queryKey: prefix }, (data) => {
    if (!data) return data;
    const delta = countDelta(data);
    return {
      ...data,
      pages: data.pages.map((page, index) => ({
        ...page,
        results: change(page.results, index === 0),
        total_count: Math.max(0, page.total_count + delta),
      })),
    };
  });
  await client.invalidateQueries({ queryKey: prefix, refetchType: "none" });
  // A first read the cancel interrupted left its table with nothing to show,
  // so that one reads again now.
  await client.refetchQueries({
    queryKey: prefix,
    type: "active",
    predicate: (query) => query.state.data === undefined,
  });
}
